import { Data, Effect, Option, Schema } from 'effect';

import { DateTimeSerializableFromString } from '../DateTimeSerializableFromString';
import { overrideSchema } from '../openapi/overrideSchema';

/**
 * Payment and method bodies are tenant-owned generated closures. The curated
 * root validates their presence while allowing each tenant to tighten those
 * fields without dragging payment-provider schemas into this package.
 */
export type PaymentRequestOverrideSlots =
  | 'PaymentRequest.payload'
  | 'PaymentRequest.payment'
  | 'PaymentRequest.method';

const JsonLdContext = Schema.optionalKey(
  Schema.Union(
    [
      Schema.String,
      Schema.Struct({
        '@vocab': Schema.String,
        hydra: Schema.Literal('http://www.w3.org/ns/hydra/core#'),
      }),
    ],
    { mode: 'oneOf' }
  ).annotate({ readOnly: true })
);
const JsonLdIri = Schema.optionalKey(Schema.String.annotate({ readOnly: true }));
const JsonLdType = Schema.optionalKey(Schema.String.annotate({ readOnly: true }));

export const PaymentRequestResponseData = Schema.Struct({
  redirect: Schema.OptionFromNullishOr(Schema.String, { onNoneEncoding: undefined }).pipe(
    Schema.withDecodingDefaultTypeKey(Effect.succeed(Option.none())),
    Schema.withConstructorDefault(Effect.succeed(Option.none()))
  ),
}).annotate({ identifier: 'PaymentRequestResponseData' });
export type PaymentRequestResponseData = Schema.Schema.Type<typeof PaymentRequestResponseData>;

/**
 * Curated plain struct replacing the generated `StructWithRest` root.
 * Its `orderToken` and `responseData` overrides therefore run their Effect v4
 * transformations instead of taking the generated rest-record fast path.
 */
export const PaymentRequest = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  hash: Schema.optionalKey(Schema.String.annotate({ readOnly: true, format: 'uuid' })),
  state: Schema.String.annotate({ default: 'new' }),
  action: Schema.String.annotate({ default: 'capture' }),
  payload: Schema.Unknown,
  responseData: Schema.Record(Schema.String, Schema.Union([Schema.String, Schema.Null])),
  payment: Schema.Unknown,
  method: Schema.Unknown,
  createdAt: DateTimeSerializableFromString,
  updatedAt: Schema.Union([DateTimeSerializableFromString, Schema.Null]),
  orderToken: Schema.optionalKey(
    Schema.Union([Schema.String, Schema.Null]).annotate({ readOnly: true })
  ),
}).annotate({ identifier: 'PaymentRequest' });
export type PaymentRequest = Schema.Schema.Type<typeof PaymentRequest>;

export const PaymentRequestExtended = overrideSchema(PaymentRequest, {
  orderToken: Schema.OptionFromNullishOr(Schema.String, { onNoneEncoding: undefined }).pipe(
    Schema.withDecodingDefaultTypeKey(Effect.succeed(Option.none())),
    Schema.withConstructorDefault(Effect.succeed(Option.none()))
  ),
  responseData: PaymentRequestResponseData,
}).annotate({ identifier: 'PaymentRequestExtended' });
export type PaymentRequestExtended = Schema.Schema.Type<typeof PaymentRequestExtended>;

export class OrderTokenNotFoundError extends Data.TaggedError(
  '@app/PaymentRequest/OrderTokenNotFoundError'
)<{
  readonly hash: string;
}> {}
